import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import {
  Wallet,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Users,
  Building2,
  BarChart3,
  Target,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ListChecks,
  Coins,
  Package,
  Hammer
} from "lucide-react";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA CAJAS
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Dashboard de saldos",
});

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ MODAL PERSONALIZADO PARA ALERTAS/CONFIRMACIONES
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
          icon: AlertCircle,
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
          icon: XCircle,
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
          icon: CheckCircle2,
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
          icon: Info,
        };
    }
  };

  const colors = getColors();
  const IconComponent = colors.icon;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IconComponent size={32} color={colors.text} />
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            className="button-hover"
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para vista detallada del conteo
function ConteoDetailModal({ conteo, cajas, onClose, isMobile }) {
  const caja = cajas.find((c) => c.id === conteo.caja_id);
  const colorVista = getVistaColor();

  const formatearMoneda = (monto, moneda) => {
    const numero = parseFloat(monto || 0);
    const formateado = numero.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${moneda} $${formateado}`;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Eye size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 4px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Vista Detallada - Conteo
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {conteo.tipo_conteo === "total"
                  ? `Conteo Total ${conteo.moneda}`
                  : `${caja?.nombre || "Caja"} ${conteo.moneda}`}{" "}
                - {new Date(conteo.fecha).toLocaleDateString()}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <BarChart3 size={20} color="#8b5cf6" />
                Información Principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Tipo de Conteo */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Tipo de Conteo
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {conteo.tipo_conteo === "total"
                      ? (<span style={{display:"inline-flex",alignItems:"center",gap:6}}><Package size={16}/> Conteo Total</span>)
                      : (<span style={{display:"inline-flex",alignItems:"center",gap:6}}><Target size={16}/> Caja Específica</span>)}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    {conteo.tipo_conteo === "total"
                      ? `Todas las cajas en ${conteo.moneda}`
                      : `Solo la caja: ${caja?.nombre || "Desconocida"}`}
                  </p>
                </div>

                {/* Moneda */}
                <div
                  style={{
                    background: conteo.moneda === "USD" ? "#f0fdf4" : "#eff6ff",
                    padding: "16px",
                    borderRadius: "12px",
                    border: `2px solid ${
                      conteo.moneda === "USD" ? "#bbf7d0" : "#dbeafe"
                    }`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: conteo.moneda === "USD" ? "#166534" : "#1e40af",
                      textTransform: "uppercase",
                    }}
                  >
                    Moneda
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: conteo.moneda === "USD" ? "#10b981" : "#3b82f6",
                    }}
                  >
                    <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                      <Coins size={16} /> {conteo.moneda === "USD" ? "USD (Dólares)" : "ARS (Pesos)"}
                    </span>
                  </p>
                </div>

                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha del Conteo
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {new Date(conteo.fecha).toLocaleDateString("es-ES", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Detalles del Conteo */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                  <ListChecks size={16}/> Detalles del Conteo
                </span>
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Monto Contado */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #2563eb)",
                    padding: "20px",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "white",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    Dinero Contado Físicamente
                  </p>
                  <p
                    style={{
                      margin: "0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: "white",
                    }}
                  >
                    {formatearMoneda(conteo.monto_contado, conteo.moneda)}
                  </p>
                </div>

                {/* Monto Sistema */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    padding: "20px",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "white",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    Según Sistema
                  </p>
                  <p
                    style={{
                      margin: "0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: "white",
                    }}
                  >
                    {formatearMoneda(conteo.monto_sistema, conteo.moneda)}
                  </p>
                </div>

                {/* Diferencia */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${
                      conteo.diferencia >= 0
                        ? "#2563eb, #2563eb"
                        : "#ef4444, #dc2626"
                    })`,
                    padding: "20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    border: `3px solid ${
                      conteo.diferencia >= 0 ? "#bbf7d0" : "#fecaca"
                    }`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "white",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    Diferencia
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: "white",
                    }}
                  >
                    {conteo.diferencia >= 0 ? "+" : ""}
                    {formatearMoneda(conteo.diferencia, conteo.moneda)}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "white",
                      opacity: 0.9,
                      fontWeight: "600",
                    }}
                  >
                    {conteo.diferencia > 0 ? (
                      <span style={{display:"inline-flex",alignItems:"center",gap:6}}><TrendingUp size={14}/> Sobra dinero</span>
                    ) : conteo.diferencia < 0 ? (
                      <span style={{display:"inline-flex",alignItems:"center",gap:6}}><AlertTriangle size={14}/> Falta dinero</span>
                    ) : (
                      <span style={{display:"inline-flex",alignItems:"center",gap:6}}><Target size={14}/> Exacto</span>
                    )}
                  </p>
                </div>

                {/* Observaciones */}
                {conteo.observaciones && (
                  <div
                    style={{
                      background: "#fefbf3",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #fbbf24",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#92400e",
                        textTransform: "uppercase",
                      }}
                    >
                      Observaciones
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {conteo.observaciones}
                    </p>
                  </div>
                )}

                {/* Información del sistema */}
                <div
                  style={{
                    background: "#f1f5f9",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #cbd5e1",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#475569",
                      textTransform: "uppercase",
                    }}
                  >
                    Información del Sistema
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "13px",
                      color: "#64748b",
                    }}
                  >
                    Creado:{" "}
                    {new Date(conteo.created_at).toLocaleString("es-ES")}
                  </p>
                  {conteo.updated_at &&
                    conteo.updated_at !== conteo.created_at && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#64748b",
                        }}
                      >
                        Modificado:{" "}
                        {new Date(conteo.updated_at).toLocaleString("es-ES")}
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Resumen del impacto */}
          <div
            style={{
              marginTop: "32px",
              padding: "20px",
              background: conteo.diferencia >= 0 ? "#f0fdf4" : "#fef2f2",
              borderRadius: "12px",
              border: `2px solid ${
                conteo.diferencia >= 0 ? "#bbf7d0" : "#fecaca"
              }`,
            }}
          >
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: conteo.diferencia >= 0 ? "#166534" : "#991b1b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                <BarChart3 size={16}/> Análisis del Conteo
              </span>
            </h4>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#1e293b",
              }}
            >
              {conteo.diferencia === 0 && (
                <p style={{ margin: 0 }}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:6}}><CheckCircle2 size={14}/></span> <strong>Conteo perfecto:</strong> El dinero físico coincide
                  exactamente con lo registrado en el sistema.
                </p>
              )}
              {conteo.diferencia > 0 && (
                <p style={{ margin: 0 }}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:6}}><TrendingUp size={14}/></span> <strong>Sobra dinero:</strong> Se encontró más dinero del
                  esperado. Esto podría indicar ingresos no registrados o
                  errores en el sistema.
                </p>
              )}
              {conteo.diferencia < 0 && (
                <p style={{ margin: 0 }}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:6}}><AlertTriangle size={14}/></span> <strong>Falta dinero:</strong> Se encontró menos dinero del
                  esperado. Revisar posibles gastos no registrados o pérdidas.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cajas({ constructora }) {
  const [cajas, setCajas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [conteos, setConteos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFinalizados, setShowFinalizados] = useState(false);
  const [showContadorModal, setShowContadorModal] = useState(false);
  const [editingConteo, setEditingConteo] = useState(null);
  const [dolarOficial, setDolarOficial] = useState(null);
  const [loadingDolar, setLoadingDolar] = useState(false);

  // Estados para vista detallada y alertas personalizadas
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedConteoDetail, setSelectedConteoDetail] = useState(null);

  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  const colorVista = getVistaColor();

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowContadorModal(false);
        setShowDetailModal(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
      cargarDolarOficial();

      // Auto-actualizar cada 30 segundos
      const interval = setInterval(cargarDatos, 30000);
      return () => clearInterval(interval);
    }
  }, [constructora]);

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // Función para abrir vista detallada
  const openDetailModal = (conteo) => {
    setSelectedConteoDetail(conteo);
    setShowDetailModal(true);
  };

  const cargarDatos = async () => {
    try {
      // Cargar cajas con información de proyectos
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select(
          `
          *,
          proyectos (
            codigo,
            apellido_cliente,
            obra_finalizada,
            mostrar_cajas,
            activo
          )
        `
        )
        .eq("constructora_id", constructora.id)
        .is("deleted_at", null)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }

      // Cargar todos los proyectos
      const { data: proyectosData, error: proyectosError } = await supabase
        .from("proyectos")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("codigo");

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);
      }

      // Cargar conteos recientes
      const { data: conteosData, error: conteosError } = await supabase
        .from("conteos_dinero")
        .select(
          `
          *,
          cajas (
            nombre,
            moneda,
            tipo_caja,
            proyectos (codigo)
          )
        `
        )
        .eq("constructora_id", constructora.id)
        .order("created_at", { ascending: false })
        .limit(5);

      if (conteosError) {
        console.error("Error cargando conteos:", conteosError);
      } else {
        setConteos(conteosData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  const cargarDolarOficial = async () => {
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setDolarOficial(data.venta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setDolarOficial(1000); // Fallback
    }
    setLoadingDolar(false);
  };

  // Organizar cajas por estructura jerárquica
  const organizarCajas = () => {
    const cajasEmpresa = cajas.filter((c) => c.tipo_caja === "empresa");
    const cajasProyecto = cajas.filter(
      (c) => c.tipo_caja === "proyecto" || c.tipo_caja === "mano_obra"
    );

    // Agrupar cajas por proyecto
    const cajasPorProyecto = {};
    cajasProyecto.forEach((caja) => {
      if (!cajasPorProyecto[caja.proyecto_id]) {
        cajasPorProyecto[caja.proyecto_id] = {
          proyecto: caja.proyectos,
          cajas: [],
        };
      }
      cajasPorProyecto[caja.proyecto_id].cajas.push(caja);
    });

    // Filtrar proyectos finalizados si corresponde
    const proyectosFiltrados = Object.entries(cajasPorProyecto).filter(
      ([_, data]) => {
        if (showFinalizados) return true;
        return data.proyecto?.activo && 
               data.proyecto?.mostrar_cajas && 
               !data.proyecto?.obra_finalizada;
      }
    );

    return {
      empresa: cajasEmpresa,
      proyectos: proyectosFiltrados,
    };
  };

  // Calcular totales
  const calcularTotales = () => {
    const cajasVisibles = showFinalizados
      ? cajas
      : cajas.filter(
          (c) => c.tipo_caja === "empresa" || 
                 (c.proyectos?.activo && 
                  c.proyectos?.mostrar_cajas && 
                  !c.proyectos?.obra_finalizada)
        );

    const totalARS = cajasVisibles
      .filter((c) => c.moneda === "ARS")
      .reduce((sum, c) => sum + parseFloat(c.balance_actual || 0), 0);

    const totalUSD = cajasVisibles
      .filter((c) => c.moneda === "USD")
      .reduce((sum, c) => sum + parseFloat(c.balance_actual || 0), 0);

    const cajasNegativas = cajasVisibles.filter(
      (c) => parseFloat(c.balance_actual || 0) < 0
    ).length;
    const proyectosActivos = proyectos.filter((p) => !p.obra_finalizada).length;

    return { totalARS, totalUSD, cajasNegativas, proyectosActivos };
  };

  const formatearMoneda = (monto, moneda) => {
    const numero = parseFloat(monto || 0);
    const formateado = numero.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${moneda} $${formateado}`;
  };

  const calcularEquivalencia = (monto, moneda) => {
    if (!dolarOficial || !monto) return "";

    const numero = parseFloat(monto);
    if (moneda === "ARS") {
      const usd = numero / dolarOficial;
      return `≈ USD $${usd.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      const ars = numero * dolarOficial;
      return `≈ ARS $${ars.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  const generarNombreCaja = (caja) => {
    return caja.nombre || `Caja ${caja.tipo_caja}`;
  };

  const handleEditarConteo = (conteo) => {
    setEditingConteo(conteo);
    setShowContadorModal(true);
  };

  const handleEliminarConteo = async (conteoId) => {
    showCustomAlert(
      "confirm",
      "¿Eliminar conteo?",
      "Esta acción no se puede deshacer.",
      async () => {
        try {
          const { error } = await supabase
            .from("conteos_dinero")
            .delete()
            .eq("id", conteoId);

          if (error) {
            console.error("Error eliminando conteo:", error);
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar conteo: " + error.message
            );
          } else {
            showCustomAlert(
              "success",
              "Éxito",
              "Conteo eliminado correctamente"
            );
            cargarDatos(); // Recargar datos
          }
        } catch (err) {
          console.error("Error:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const cajasOrganizadas = organizarCajas();
  const totales = calcularTotales();

  // ✅ STATS DATA PARA RESUMEN COMPACTO
  const statsData = [
    {
      label: "Total ARS",
      value: `$${totales.totalARS.toLocaleString()}`,
      icon: DollarSign,
      color: colorVista.solid,
    },
    {
      label: "Total USD",
      value: `US$${totales.totalUSD.toLocaleString()}`,
      icon: DollarSign,
      color: "#8b5cf6",
    },
    {
      label: "Proyectos Activos",
      value: totales.proyectosActivos.toString(),
      icon: Building2,
      color: "#0ea5e9",
    },
    {
      label: "Cajas en Negativo",
      value: totales.cajasNegativas.toString(),
      icon: AlertCircle,
      color: totales.cajasNegativas > 0 ? "#ef4444" : colorVista.solid,
    },
  ];

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                flexShrink: 0,
              }}
            >
              <Wallet size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Cajas
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                  Dashboard de saldos en tiempo real {loadingDolar && <Loader2 size={14} />}
                </span>
              </p>
            </div>
          </div>

          {/* Botón desktop unificado - SIEMPRE VISIBLE */}
          {!isMobile && (
            <button
              onClick={() => {
                setEditingConteo(null);
                setShowContadorModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={16} />
              Contar Dinero
            </button>
          )}
        </div>
      </div>

      {/* ✅ STATS COMPACTAS (SOLO DESKTOP) */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {statsData.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Controles */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            fontSize: "13px",
            color: "#64748b",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}><DollarSign size={14}/> Dólar oficial: ${dolarOficial || "---"}</span>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showFinalizados}
            onChange={(e) => setShowFinalizados(e.target.checked)}
            style={{
              width: "16px",
              height: "16px",
              accentColor: colorVista.solid,
            }}
          />
          Mostrar proyectos finalizados
        </label>
      </div>

      {/* ✅ CONTENIDO PRINCIPAL */}
      <div
        className="main-content"
        style={{
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 20px",
              background: "white",
              borderRadius: "16px",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #3b82f6",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Cargando...
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Cajas de Empresa */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                  color: "white",
                  padding: isMobile ? "16px 20px" : "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span style={{ display:"inline-flex" }}><Building2 size={20}/></span>
                <div>
                  <h3
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "600",
                    }}
                  >
                    Cajas de Empresa
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      opacity: 0.8,
                    }}
                  >
                    Dinero general de {constructora?.nombre}
                  </p>
                </div>
              </div>

              <div style={{ padding: isMobile ? "16px" : "20px" }}>
                {cajasOrganizadas.empresa.length === 0 ? (
                  <p
                    style={{
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "14px",
                      margin: 0,
                      padding: "20px",
                    }}
                  >
                    No hay cajas de empresa
                  </p>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    {cajasOrganizadas.empresa.map((caja, index) => (
                      <CajaCard
                        key={caja.id}
                        caja={caja}
                        dolarOficial={dolarOficial}
                        isMobile={isMobile}
                        index={index}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Cajas por Proyecto */}
            {cajasOrganizadas.proyectos.map(
              ([proyectoId, data], proyectoIndex) => (
                <div
                  key={proyectoId}
                  style={{
                    background: "white",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    border: "1px solid #f1f5f9",
                    opacity: data.proyecto?.obra_finalizada ? 0.7 : 1,
                    animation: `slideInRight 0.${
                      6 + proyectoIndex
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  <div
                    style={{
                      background: data.proyecto?.obra_finalizada
                        ? "linear-gradient(135deg, #64748b 0%, #475569 100%)"
                        : "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                      color: "white",
                      padding: isMobile ? "16px 20px" : "20px 24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ display:"inline-flex" }}><Building2 size={20}/></span>
                    <div>
                      <h3
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: isMobile ? "16px" : "18px",
                          fontWeight: "600",
                        }}
                      >
                        Proyecto {data.proyecto?.codigo}
                        {data.proyecto?.obra_finalizada && (
                          <span
                            style={{
                              fontSize: "12px",
                              background: "rgba(255,255,255,0.2)",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            FINALIZADO
                          </span>
                        )}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          opacity: 0.8,
                        }}
                      >
                        Cliente: {data.proyecto?.apellido_cliente}
                      </p>
                    </div>
                  </div>

                  <div style={{ padding: isMobile ? "16px" : "20px" }}>
                    {/* Separar cajas de proyecto y mano de obra */}
                    {(() => {
                      const cajasProyecto = data.cajas.filter(
                        (c) => c.tipo_caja === "proyecto"
                      );
                      const cajasManoObra = data.cajas.filter(
                        (c) => c.tipo_caja === "mano_obra"
                      );

                      return (
                        <div style={{ display: "grid", gap: "16px" }}>
                          {/* Cajas del Proyecto */}
                          {cajasProyecto.length > 0 && (
                            <div>
                              <h4
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#64748b",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span style={{display:"inline-flex",alignItems:"center",gap:6}}><Hammer size={16}/></span> Cajas del Proyecto
                              </h4>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: isMobile
                                    ? "1fr"
                                    : "repeat(auto-fit, minmax(280px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                {cajasProyecto.map((caja, index) => (
                                  <CajaCard
                                    key={caja.id}
                                    caja={caja}
                                    dolarOficial={dolarOficial}
                                    isMobile={isMobile}
                                    index={index}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Cajas de Mano de Obra */}
                          {cajasManoObra.length > 0 && (
                            <div>
                              <h4
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#64748b",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span style={{display:"inline-flex",alignItems:"center",gap:6}}><Users size={16}/></span> Cajas de Mano de Obra
                              </h4>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: isMobile
                                    ? "1fr"
                                    : "repeat(auto-fit, minmax(280px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                {cajasManoObra.map((caja, index) => (
                                  <CajaCard
                                    key={caja.id}
                                    caja={caja}
                                    dolarOficial={dolarOficial}
                                    isMobile={isMobile}
                                    index={index}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {/* Conteos Recientes */}
        {conteos.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "20px",
              marginTop: "24px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "1px solid #f1f5f9",
              animation: "slideInRight 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <BarChart3 size={20} color={colorVista.solid} />
              Conteos Recientes
            </h3>

            <div
              style={{
                display: "grid",
                gap: "8px",
              }}
            >
              {conteos.map((conteo, index) => (
                <div
                  key={conteo.id}
                  style={{
                    background: "#f8fafc",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                    animation: `slideInRight 0.${
                      10 + index
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        margin: "0 0 2px 0",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {conteo.tipo_conteo === "total"
                        ? `Conteo Total ${conteo.moneda}`
                        : `${conteo.cajas?.nombre || "Caja"} ${conteo.moneda}`}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "11px",
                        color: "#64748b",
                      }}
                    >
                      {new Date(conteo.fecha).toLocaleDateString()} - Contado:{" "}
                      {formatearMoneda(conteo.monto_contado, conteo.moneda)} -
                      Sistema:{" "}
                      {formatearMoneda(conteo.monto_sistema, conteo.moneda)}
                      {conteo.observaciones && (
                        <span style={{ fontStyle: "italic" }}>
                          {" "}
                          - {conteo.observaciones}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: conteo.diferencia >= 0 ? "#059669" : "#ef4444",
                      }}
                    >
                      {conteo.diferencia >= 0 ? "+" : ""}
                      {formatearMoneda(conteo.diferencia, conteo.moneda)}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                      }}
                    >
                      {/* Botón para ver detalles completos */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailModal(conteo);
                        }}
                        className="action-button"
                        style={{
                          background: "#eff6ff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px 6px",
                          cursor: "pointer",
                          color: "#1d4ed8",
                          fontSize: "11px",
                          transition: "all 0.2s ease",
                          touchAction: "manipulation",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#dbeafe";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#eff6ff";
                        }}
                      >
                        <Eye size={12} />
                      </button>

                      <button
                        onClick={() => handleEditarConteo(conteo)}
                        className="action-button"
                        style={{
                          background: "#f1f5f9",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px 6px",
                          cursor: "pointer",
                          color: "#475569",
                          fontSize: "11px",
                          transition: "all 0.2s ease",
                          touchAction: "manipulation",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#e2e8f0";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#f1f5f9";
                        }}
                      >
                        <Edit size={12} />
                      </button>
                      <button
                        onClick={() => handleEliminarConteo(conteo.id)}
                        className="action-button"
                        style={{
                          background: "#fef2f2",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px 6px",
                          cursor: "pointer",
                          color: "#dc2626",
                          fontSize: "11px",
                          transition: "all 0.2s ease",
                          touchAction: "manipulation",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#fee2e2";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fef2f2";
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ✅ BOTÓN FLOTANTE MÓVIL UNIFICADO */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingConteo(null);
              setShowContadorModal(true);
            }}
            className="button-hover"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Modal Contador de Dinero */}
      {showContadorModal && (
        <ContadorDineroModal
          constructora={constructora}
          cajas={cajas}
          dolarOficial={dolarOficial}
          conteo={editingConteo}
          onClose={() => {
            setShowContadorModal(false);
            setEditingConteo(null);
          }}
          onSave={() => {
            setShowContadorModal(false);
            setEditingConteo(null);
            cargarDatos();
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedConteoDetail && (
        <ConteoDetailModal
          conteo={selectedConteoDetail}
          cajas={cajas}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedConteoDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* ✅ MODAL PERSONALIZADO PARA ALERTAS CON ESC HANDLER */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }
      `}</style>
    </div>
  );
}

// ✅ COMPONENTE PARA CADA TARJETA DE CAJA CON ANIMACIONES
function CajaCard({ caja, dolarOficial, isMobile, index = 0 }) {
  const saldo = parseFloat(caja.balance_actual || 0);
  const esNegativo = saldo < 0;

  const formatearMoneda = (monto, moneda) => {
    const numero = parseFloat(monto || 0);
    const formateado = numero.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${moneda} $${formateado}`;
  };

  const calcularEquivalencia = (monto, moneda) => {
    if (!dolarOficial || !monto) return "";

    const numero = parseFloat(monto);
    if (moneda === "ARS") {
      const usd = numero / dolarOficial;
      return `≈ USD $${usd.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      const ars = numero * dolarOficial;
      return `≈ ARS $${ars.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  const generarNombreCaja = (caja) => {
    return caja.nombre || `Caja ${caja.tipo_caja}`;
  };

  return (
    <div
      style={{
        background: esNegativo ? "#fef2f2" : "#f8fafc",
        border: esNegativo ? "2px solid #fecaca" : "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: isMobile ? "14px" : "16px",
        transition: "all 0.2s ease",
        animation: `slideInRight 0.${6 + index}s cubic-bezier(0.4, 0, 0.2, 1)`,
        cursor: "pointer",
      }}
      className="stat-card"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <h4
          style={{
            margin: 0,
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          {generarNombreCaja(caja)}
        </h4>

        <span
          style={{
            fontSize: "18px",
            opacity: 0.6,
          }}
        >
          <Coins size={18} />
        </span>
      </div>

      <div
        style={{
          textAlign: "center",
          marginBottom: "8px",
        }}
      >
        <p
          style={{
            margin: "0 0 4px 0",
            fontSize: isMobile ? "18px" : "20px",
            fontWeight: "700",
            color: esNegativo ? "#dc2626" : "#059669",
          }}
        >
          {formatearMoneda(saldo, caja.moneda)}
        </p>

        {dolarOficial && (
          <p
            style={{
              margin: 0,
              fontSize: "11px",
              color: "#64748b",
              fontWeight: "500",
            }}
          >
            {calcularEquivalencia(saldo, caja.moneda)}
          </p>
        )}
      </div>

      {esNegativo && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            padding: "6px 8px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "#dc2626",
              fontWeight: "600",
            }}
          >
            <span style={{display:"inline-flex",alignItems:"center",gap:6}}><AlertTriangle size={12}/></span> SALDO NEGATIVO
          </span>
        </div>
      )}
    </div>
  );
}

// ✅ MODAL PARA CONTAR DINERO CON DISEÑO UNIFICADO
function ContadorDineroModal({
  constructora,
  cajas,
  dolarOficial,
  conteo,
  onClose,
  onSave,
  isMobile,
  showCustomAlert,
}) {
  const [formData, setFormData] = useState({
    tipo_conteo: "total",
    caja_id: "",
    moneda: "ARS",
    monto_contado: "",
    fecha: new Date().toISOString().split("T")[0],
    observaciones: "",
  });

  const [guardando, setGuardando] = useState(false);
  const [montoSistema, setMontoSistema] = useState(0);

  const colorVista = getVistaColor();

  useEffect(() => {
    if (conteo) {
      // Modo edición - cargar datos del conteo existente
      setFormData({
        tipo_conteo: conteo.tipo_conteo,
        caja_id: conteo.caja_id || "",
        moneda: conteo.moneda,
        monto_contado: conteo.monto_contado.toString(),
        fecha: conteo.fecha,
        observaciones: conteo.observaciones || "",
      });
      setMontoSistema(parseFloat(conteo.monto_sistema));
    }
  }, [conteo]);

  useEffect(() => {
    if (!conteo) {
      // Solo calcular automáticamente si no estamos editando
      calcularMontoSistema();
    }
  }, [formData.tipo_conteo, formData.caja_id, formData.moneda, cajas, conteo]);

  const calcularMontoSistema = () => {
    if (formData.tipo_conteo === "total") {
      const total = cajas
        .filter((c) => c.moneda === formData.moneda)
        .reduce((sum, c) => sum + parseFloat(c.balance_actual || 0), 0);
      setMontoSistema(total);
    } else if (formData.caja_id) {
      const caja = cajas.find((c) => c.id === formData.caja_id);
      setMontoSistema(parseFloat(caja?.balance_actual || 0));
    } else {
      setMontoSistema(0);
    }
  };

  const formatearMoneda = (monto, moneda) => {
    const numero = parseFloat(monto || 0);
    const formateado = numero.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${moneda} $${formateado}`;
  };

  const calcularDiferencia = () => {
    const contado = parseFloat(formData.monto_contado || 0);
    return contado - montoSistema;
  };

  const generarNombreCaja = (caja) => {
    return caja.nombre || `Caja ${caja.tipo_caja}`;
  };

  const handleSave = async () => {
    if (!formData.monto_contado) {
      showCustomAlert("error", "Error", "Por favor ingresa el monto contado");
      return;
    }

    if (formData.tipo_conteo === "caja_especifica" && !formData.caja_id) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor selecciona una caja específica"
      );
      return;
    }

    if (!formData.fecha) {
      showCustomAlert("error", "Error", "Por favor selecciona una fecha");
      return;
    }

    setGuardando(true);

    try {
      const dataToSave = {
        constructora_id: constructora.id,
        fecha: formData.fecha,
        tipo_conteo: formData.tipo_conteo,
        caja_id:
          formData.tipo_conteo === "caja_especifica" ? formData.caja_id : null,
        monto_contado: parseFloat(formData.monto_contado),
        monto_sistema: montoSistema,
        moneda: formData.moneda,
        observaciones: formData.observaciones.trim() || null,
      };

      let result;
      if (conteo) {
        // Actualizar conteo existente
        result = await supabase
          .from("conteos_dinero")
          .update(dataToSave)
          .eq("id", conteo.id)
          .select();
      } else {
        // Crear nuevo conteo
        result = await supabase
          .from("conteos_dinero")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando conteo:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        showCustomAlert(
          "success",
          "Éxito",
          conteo
            ? "Conteo actualizado correctamente"
            : "Conteo guardado correctamente"
        );
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* ✅ HEADER UNIFICADO */}
        <div
          style={{
            background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <BarChart3 size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {conteo ? "Editar Conteo" : "Contador de Dinero"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {conteo
                  ? "Modificar conteo existente"
                  : "Verificar dinero físico vs sistema"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "25px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Tipo de Conteo */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de Conteo *
              </label>
              <CustomDropdown
                value={formData.tipo_conteo}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    tipo_conteo: value,
                    caja_id: "", // Reset caja cuando cambia tipo
                  })
                }
                options={[
                  { value: "total", label: "Contar Todo el Dinero" },
                  { value: "caja_especifica", label: "Contar Caja Específica" },
                ]}
                placeholder="Selecciona tipo de conteo"
                disabled={!!conteo} // Deshabilitar si estamos editando
                icon={BarChart3}
              />
            </div>

            {/* Fecha */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Calendar size={16} />
                Fecha *
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData({ ...formData, fecha: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  background: "white",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Moneda */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <DollarSign size={16} />
                Moneda *
              </label>
              <CustomDropdown
                value={formData.moneda}
                onChange={(value) =>
                  setFormData({ ...formData, moneda: value })
                }
                options={[
                  { value: "ARS", label: "ARS (Pesos)" },
                  { value: "USD", label: "USD (Dólares)" },
                ]}
                placeholder="Selecciona moneda"
                disabled={!!conteo} // Deshabilitar si estamos editando
                icon={DollarSign}
              />
            </div>

            {/* Caja Específica */}
            {formData.tipo_conteo === "caja_especifica" && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Wallet size={16} />
                  Caja a Contar *
                </label>
                <CustomDropdown
                  value={formData.caja_id}
                  onChange={(value) =>
                    setFormData({ ...formData, caja_id: value })
                  }
                  options={[
                    { value: "", label: "Selecciona una caja" },
                    ...cajas
                      .filter((c) => c.moneda === formData.moneda)
                      .map((caja) => ({
                        value: caja.id,
                        label: `${generarNombreCaja(caja)} - ${formatearMoneda(
                          caja.balance_actual,
                          caja.moneda
                        )}`,
                      })),
                  ]}
                  placeholder="Selecciona una caja"
                  disabled={!!conteo} // Deshabilitar si estamos editando
                  icon={Wallet}
                />
              </div>
            )}

            {/* Comparación */}
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #0ea5e9",
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#0369a1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <BarChart3 size={16} />
                Comparación
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Según Sistema
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#0369a1",
                    }}
                  >
                    {formatearMoneda(montoSistema, formData.moneda)}
                  </p>
                </div>

                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Diferencia
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: calcularDiferencia() >= 0 ? "#059669" : "#dc2626",
                    }}
                  >
                    {calcularDiferencia() >= 0 ? "+" : ""}
                    {formatearMoneda(calcularDiferencia(), formData.moneda)}
                  </p>
                </div>
              </div>

              {calcularDiferencia() !== 0 && formData.monto_contado && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    background:
                      calcularDiferencia() > 0 ? "#dcfce7" : "#fee2e2",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: calcularDiferencia() > 0 ? "#166534" : "#dc2626",
                    }}
                  >
                    {calcularDiferencia() > 0
                      ? "✅ Sobra dinero"
                      : "⚠️ Falta dinero"}
                  </span>
                </div>
              )}
            </div>

            {/* Monto Contado */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Dinero Contado Físicamente *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monto_contado}
                onChange={(e) =>
                  setFormData({ ...formData, monto_contado: e.target.value })
                }
                placeholder="0.00"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  background: "white",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Observaciones */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Observaciones
              </label>
              <textarea
                value={formData.observaciones}
                onChange={(e) =>
                  setFormData({ ...formData, observaciones: e.target.value })
                }
                placeholder="Notas adicionales del conteo..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  background: "white",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>
          </div>
        </div>

        {/* ✅ FOOTER UNIFICADO */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 20px" : "20px 25px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 16px" : "12px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <X size={16} />
            Cancelar
          </button>

          <button
            onClick={handleSave}
            disabled={guardando}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 20px" : "12px 24px",
              border: "none",
              borderRadius: "10px",
              background: guardando
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: guardando ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                {conteo ? "Actualizar Conteo" : "Guardar Conteo"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Cajas;
